import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type User } from '../api/client';

interface Props {
  valor: string;
  onElegir: (userId: string) => void;
  etiqueta: string;
}

/**
 * Buscador de personas por nombre.
 *
 * Un desplegable con toda la plantilla deja de servir en cuanto la caja pasa de
 * unas decenas de empleados: hay que recorrerlo entero para encontrar a alguien.
 * Aquí se escribe el nombre y la búsqueda ocurre **en el servidor**, que es
 * quien puede buscar sobre toda la plantilla —el cliente sólo tendría la primera
 * página.
 *
 * El valor que se guarda es el identificador, nunca el nombre: filtrar por texto
 * libre devolvería resultados distintos si mañana alguien cambia de apellido.
 */
export function BuscadorDePersona({ valor, onElegir, etiqueta }: Props) {
  const { t } = useTranslation();
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<User[]>([]);
  const [elegida, setElegida] = useState<User | null>(null);
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  // Quién está elegido ahora: puede venir de la URL sin haber pasado por aquí.
  useEffect(() => {
    if (!valor) {
      setElegida(null);
      return;
    }
    if (elegida?.id === valor) return;
    api
      .users(`?pageSize=100`)
      .then((pagina) => setElegida(pagina.items.find((u) => u.id === valor) ?? null))
      .catch(() => setElegida(null));
  }, [valor, elegida?.id]);

  // Se espera a que la persona deje de escribir: una consulta por tecla castiga
  // al servidor y devuelve resultados que ya nadie está mirando.
  useEffect(() => {
    if (texto.trim().length < 2) {
      setResultados([]);
      return;
    }
    const temporizador = window.setTimeout(() => {
      api
        .users(`?q=${encodeURIComponent(texto.trim())}&pageSize=10`)
        .then((pagina) => setResultados(pagina.items))
        .catch(() => setResultados([]));
    }, 250);
    return () => window.clearTimeout(temporizador);
  }, [texto]);

  // Un clic fuera cierra la lista; si no, queda flotando sobre el resto.
  useEffect(() => {
    const alClicar = (evento: MouseEvent) => {
      if (!contenedor.current?.contains(evento.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', alClicar);
    return () => document.removeEventListener('mousedown', alClicar);
  }, []);

  const id = useMemo(() => `persona-${Math.random().toString(36).slice(2, 8)}`, []);

  return (
    <div className="campo buscador" ref={contenedor}>
      <label htmlFor={id}>{etiqueta}</label>

      {elegida ? (
        <div className="buscador__elegida">
          <span>
            {elegida.fullName}
            <span className="tenue mono buscador__usuario">{elegida.username}</span>
          </span>
          <button
            className="boton boton--fantasma"
            type="button"
            onClick={() => {
              onElegir('');
              setTexto('');
            }}
          >
            {t('search.clear')}
          </button>
        </div>
      ) : (
        <>
          <input
            id={id}
            type="search"
            value={texto}
            placeholder={t('search.personPlaceholder')}
            autoComplete="off"
            role="combobox"
            aria-expanded={abierto && resultados.length > 0}
            aria-controls={`${id}-lista`}
            onChange={(e) => {
              setTexto(e.target.value);
              setAbierto(true);
            }}
            onFocus={() => setAbierto(true)}
          />
          {abierto && texto.trim().length >= 2 && (
            <ul className="buscador__lista" id={`${id}-lista`} role="listbox">
              {resultados.length === 0 ? (
                <li className="buscador__vacio">{t('search.noPeople')}</li>
              ) : (
                resultados.map((persona) => (
                  <li key={persona.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      className="buscador__opcion"
                      onClick={() => {
                        onElegir(persona.id);
                        setElegida(persona);
                        setAbierto(false);
                        setTexto('');
                      }}
                    >
                      {persona.fullName}
                      <span className="tenue mono buscador__usuario">{persona.username}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
